import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { PageHeader } from '@/shared/components/PageHeader';
import { cn } from '@/lib/utils';
import { usePermissions } from '@/shared/hooks/usePermissions';
import { UI_LABELS } from '@/config/ui-labels';

/**
 * Settings sub-nav — only implemented surfaces.
 * Placeholder tabs (Security, Credentials, Notifications, General, Tenant)
 * are hidden from navigation; routes remain for compatibility.
 */
const SETTINGS_TABS = [
  { label: UI_LABELS.myAccount, path: 'account', permission: 'account:edit' as const },
  {
    label: UI_LABELS.organization,
    path: 'organization',
    permission: 'settings:view' as const,
    adminOnly: true,
  },
];

/** Settings layout with sub-navigation and page header. */
export function SettingsLayout() {
  const { can, role } = usePermissions();
  const location = useLocation();
  const onOrganization = location.pathname.startsWith('/settings/organization');

  const tabs = SETTINGS_TABS.filter((tab) => {
    if (tab.adminOnly && role === 'USER') return false;
    return can(tab.permission);
  });

  const title = onOrganization ? UI_LABELS.organizationSettings : 'Settings';
  const description = onOrganization
    ? 'View organization details for this VEE Connector account.'
    : 'Manage your personal account.';

  return (
    <div className="space-y-6">
      <PageHeader
        title={title}
        description={description}
        breadcrumbs={[
          {
            label: onOrganization ? UI_LABELS.organization : 'Settings',
          },
        ]}
      />
      <div className="flex flex-col gap-6 md:flex-row">
        {tabs.length > 1 ? (
          <nav className="flex shrink-0 flex-row gap-1 overflow-x-auto md:w-48 md:flex-col md:overflow-visible">
            {tabs.map((tab) => (
              <NavLink
                key={tab.path}
                to={`/settings/${tab.path}`}
                className={({ isActive }) =>
                  cn(
                    'rounded-md px-3 py-2 text-sm font-medium whitespace-nowrap',
                    isActive
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-muted'
                  )
                }
              >
                {tab.label}
              </NavLink>
            ))}
          </nav>
        ) : null}
        <div className="min-w-0 flex-1">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
